import { useLayoutEffect, useState, RefObject } from "react";

export interface HighlightStyle {
  left: string;
  width: string;
}

export function useSearchHighlight(
  text: string,
  searchValue: string | undefined,
  textRef: RefObject<HTMLElement>
): HighlightStyle[] {
  const [highlightStyles, setHighlightStyles] = useState<HighlightStyle[]>([]);

  useLayoutEffect(() => {
    if (!searchValue || !textRef.current) {
      setHighlightStyles([]);
      return;
    }

    const lowerText = text.toLowerCase();
    const lowerSearch = searchValue.toLowerCase();

    // Find all matches
    const matches: Array<{ start: number; end: number }> = [];
    let searchIndex = 0;

    while (searchIndex < lowerText.length) {
      const matchIndex = lowerText.indexOf(lowerSearch, searchIndex);
      if (matchIndex === -1) break;

      matches.push({ start: matchIndex, end: matchIndex + searchValue.length });
      searchIndex = matchIndex + searchValue.length;
    }

    if (matches.length === 0) {
      setHighlightStyles([]);
      return;
    }

    // Merge overlapping or adjacent matches
    const mergedMatches: Array<{ start: number; end: number }> = [];
    for (const match of matches) {
      if (mergedMatches.length === 0) {
        mergedMatches.push(match);
        continue;
      }

      const lastMatch = mergedMatches[mergedMatches.length - 1];

      // Check if current match overlaps or is adjacent to the last merged match
      if (match.start <= lastMatch.end + 1) {
        // Merge by extending the last match
        lastMatch.end = Math.max(lastMatch.end, match.end);
      } else {
        // Add as a new separate match
        mergedMatches.push(match);
      }
    }

    // Create a canvas to measure text width
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return;

    // Get computed style of the text element
    const computedStyle = window.getComputedStyle(textRef.current);
    context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;

    // Calculate positions for all merged matches
    const styles = mergedMatches.map((match) => {
      const beforeText = text.slice(0, match.start);
      const beforeWidth = context.measureText(beforeText).width;

      const matchText = text.slice(match.start, match.end);
      const matchWidth = context.measureText(matchText).width;

      // Check if match is at word boundaries
      const isStartOfWord = match.start === 0 || /\s/.test(text[match.start - 1]);
      const isEndOfWord = match.end === text.length || /\s/.test(text[match.end]);

      // Add padding if at word boundaries
      const paddingLeft = isStartOfWord ? 2 : 0;
      const paddingRight = isEndOfWord ? 2 : 0;

      return {
        left: `${beforeWidth - paddingLeft}px`,
        width: `${matchWidth + paddingLeft + paddingRight}px`,
      };
    });

    setHighlightStyles(styles);
  }, [text, searchValue, textRef]);

  return highlightStyles;
}
